import React from "react";
import {
  FaEnvelope,
  FaExclamationTriangle,
  FaPhoneAlt,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import { departementsEtRegions } from "../../../../utils/departements";
import ModalShell from "./ModalShell";

const inputClassName =
  "min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:focus:bg-white/[0.08]";

const inputWithIconClassName = `${inputClassName} !pl-12 pr-4`;

const inputWithIconWrapperClassName =
  "flex min-h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-sky-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-sky-500/10 dark:border-white/10 dark:bg-white/[0.06] dark:focus-within:bg-white/[0.08]";

const inputInsideIconWrapperClassName =
  "min-w-0 flex-1 border-0 bg-transparent py-3 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-white/35";

const getAvailableRegions = (country) =>
  departementsEtRegions.filter((dep) => dep.pays === country);

function Field({ label, description, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-white/80">
        {label}
      </label>

      {children}

      {description && (
        <p className="mt-2 text-xs font-medium leading-5 text-slate-500 dark:text-white/45">
          {description}
        </p>
      )}
    </div>
  );
}

function EditJudokaModal({
  judoka,
  form,
  onChange,
  onCountryChange,
  onSubmit,
  onClose,
  saving,
  t,
}) {
  const regions = getAvailableRegions(form.pays);

  return (
    <ModalShell
      title={
        judoka
          ? t("judokaProfile.editProfile", {
              defaultValue: "Modifier le profil judoka",
            })
          : t("judokaProfile.createProfile", {
              defaultValue: "Créer un profil judoka",
            })
      }
      eyebrow={judoka ? "Identité sportive" : "Nouveau judoka"}
      onClose={onClose}
    >
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("judokaProfile.firstName", {
              defaultValue: "Prénom",
            })}
          >
            <input
              type="text"
              name="prenom"
              value={form.prenom || ""}
              onChange={onChange}
              autoComplete="given-name"
              className={inputClassName}
              required
            />
          </Field>

          <Field
            label={t("judokaProfile.lastName", {
              defaultValue: "Nom",
            })}
          >
            <input
              type="text"
              name="nom"
              value={form.nom || ""}
              onChange={onChange}
              autoComplete="family-name"
              className={inputClassName}
              required
            />
          </Field>

          <Field
            label={t("judokaProfile.gender", {
              defaultValue: "Genre",
            })}
          >
            <select
              name="genre"
              value={form.genre || ""}
              onChange={onChange}
              className={inputClassName}
              required
            >
              <option value="">
                {t("judokaProfile.selectGender", {
                  defaultValue: "Sélectionnez un genre",
                })}
              </option>

              <option value="Masculin">
                {t("judokaProfile.male", {
                  defaultValue: "Masculin",
                })}
              </option>

              <option value="Féminin">
                {t("judokaProfile.female", {
                  defaultValue: "Féminin",
                })}
              </option>
            </select>
          </Field>

          <Field
            label={t("judokaProfile.birthDate", {
              defaultValue: "Date de naissance",
            })}
          >
            <input
              type="date"
              name="date_naissance"
              value={form.date_naissance || ""}
              onChange={onChange}
              className={inputClassName}
              required
            />
          </Field>

          <Field
            label={`${t("judokaProfile.weight", {
              defaultValue: "Poids",
            })} (kg)`}
          >
            <input
              type="number"
              name="poids"
              value={form.poids ?? ""}
              onChange={onChange}
              className={inputClassName}
              min="0"
              step="0.1"
            />
          </Field>

          <Field
            label={t("judokaProfile.license", {
              defaultValue: "Numéro de licence",
            })}
          >
            <input
              type="text"
              name="licence_number"
              value={form.licence_number || ""}
              onChange={onChange}
              className={inputClassName}
            />
          </Field>

          <Field label="Lien vers la licence">
            <input
              type="url"
              name="licence_url"
              value={form.licence_url || ""}
              onChange={onChange}
              placeholder="https://exemple.com/licence.pdf"
              className={inputClassName}
            />
          </Field>

          <Field
            label={t("judokaProfile.club", {
              defaultValue: "Club",
            })}
          >
            <input
              type="text"
              name="club"
              value={form.club || ""}
              onChange={onChange}
              className={inputClassName}
            />
          </Field>

          <Field
            label={t("judokaProfile.country", {
              defaultValue: "Pays",
            })}
          >
            <select
              name="pays"
              value={form.pays || ""}
              onChange={onCountryChange}
              className={inputClassName}
              required
            >
              <option value="">Sélectionnez votre pays</option>

              <option value="France">France</option>

              <option value="Belgique">Belgique</option>

              <option value="Luxembourg">Luxembourg</option>

              <option value="Suisse">Suisse</option>
            </select>
          </Field>

          <div className="sm:col-span-2">
            <Field
              label={t("judokaProfile.region", {
                defaultValue: "Département / province",
              })}
            >
              <select
                name="departement"
                value={form.departement || ""}
                onChange={onChange}
                className={inputClassName}
                disabled={!form.pays}
              >
                <option value="">Sélectionnez un département/province</option>

                {regions.map((dep) => (
                  <option key={dep.code} value={dep.code}>
                    {dep.departement}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        <section className="overflow-hidden rounded-[1.75rem] border border-red-200 bg-red-50/70 dark:border-red-400/20 dark:bg-red-500/[0.07]">
          <div className="flex items-start gap-3 border-b border-red-200/70 p-4 dark:border-red-400/15 sm:p-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-200">
              <FaExclamationTriangle />
            </div>

            <div>
              <h3 className="font-titre text-lg font-bold text-slate-950 dark:text-white">
                Coordonnées de contact
              </h3>

              <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/55">
                Ces informations permettront à l’enseignant de contacter
                rapidement le judoka ou son responsable en cas de besoin.
              </p>
            </div>
          </div>

          <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
            <Field
              label="Téléphone"
              description="Champ facultatif. Les espaces et l’indicatif international sont acceptés."
            >
              <div className={inputWithIconWrapperClassName}>
                <FaPhoneAlt className="shrink-0 text-sm text-slate-400 dark:text-white/40" />

                <input
                  type="tel"
                  name="tel_adherent"
                  value={form.tel_adherent || ""}
                  onChange={onChange}
                  autoComplete="tel"
                  maxLength={30}
                  placeholder="06 12 34 56 78"
                  className={inputInsideIconWrapperClassName}
                />
              </div>
            </Field>

            <Field
              label="Adresse e-mail"
              description="Champ facultatif. Un bouton d’envoi rapide sera proposé à l’enseignant."
            >
              <div className={inputWithIconWrapperClassName}>
                <FaEnvelope className="shrink-0 text-sm text-slate-400 dark:text-white/40" />

                <input
                  type="email"
                  name="mail_adherent"
                  value={form.mail_adherent || ""}
                  onChange={onChange}
                  autoComplete="email"
                  maxLength={255}
                  placeholder="contact@exemple.fr"
                  className={inputInsideIconWrapperClassName}
                />
              </div>
            </Field>
          </div>
        </section>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10"
          >
            <FaTimes />

            {t("judokaProfile.cancel", {
              defaultValue: "Annuler",
            })}
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-orange-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />

            {saving
              ? "Enregistrement..."
              : t("judokaProfile.save", {
                  defaultValue: "Enregistrer",
                })}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

export default EditJudokaModal;
